import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { useState } from "react";

import type { WebAuthnCredentialResponse } from "../../../api";

import { apiGetMfaStatusOptions } from "../../../api";
import { ApiConnector } from "../../../api/api-connector";
import { errorText } from "../../../app/error-text";
import { Tooltip } from "../../../components/ui-kit/base/tooltip";
import { Button } from "../../../components/ui-kit/button";
import { Dialog, DialogBody } from "../../../components/ui-kit/dialog";
import { useDialog } from "../../../components/ui-kit/dialog-provider";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { Spinner } from "../../../components/ui-kit/spinner";
import { BackupCodes } from "./backup-codes";
import { MfaEnrollment } from "./mfa-enrollment";
import { usePasskeySupport } from "./passkey";
import { PasskeyList } from "./passkey-list";

/** Which dialog, if any, is open over the settings rows. */
type OpenDialog =
  | { kind: "none" }
  /** `replacing` only changes the wording — the endpoints are the same. */
  | { kind: "enroll"; method: "totp"; replacing: boolean }
  | { kind: "enroll"; method: "passkey" }
  | { kind: "backupCodes"; codes: string[] };

/**
 * The two-factor rows on the account page: what is set up, and the two things
 * a user can do about it. Every write here is gated on a fresh token, so the
 * password prompt is a normal part of the flow rather than an edge case.
 */
export function MfaSettings({ api }: { api: ApiConnector }) {
  const {
    data: status,
    error: statusError,
    refetch,
  } = useQuery({
    ...apiGetMfaStatusOptions(),
    meta: { handlesOwnErrors: true },
  });
  const [dialog, setDialog] = useState<OpenDialog>({ kind: "none" });
  const passkeySupport = usePasskeySupport();
  const { t } = useLingui();
  const { confirm } = useDialog();

  async function regenerateBackupCodes() {
    const { backup_codes } = await api.regenerateBackupCodes();
    setDialog({ kind: "backupCodes", codes: backup_codes });
    // The old codes are dead now, so the count has to come from the server.
    await refetch();
  }

  // Backup codes only recover a method, so they go with the last one.
  const backupCodesGoMessage = t`Your backup codes will be deleted with it. You can set two-factor authentication up again at any time.`;

  /** @param lastMethod whether nothing else is set up, so the codes go too. */
  async function removeTotp(lastMethod: boolean) {
    const confirmed = await confirm(
      lastMethod
        ? backupCodesGoMessage
        : t`You can set it up again at any time.`,
      t`Remove your authenticator app?`,
      { confirmLabel: t`Remove`, destructive: true },
    );

    if (!confirmed) {
      return;
    }

    await api.removeTotp();
    await refetch();
  }

  /** @param lastMethod whether nothing else is set up, so the codes go too. */
  async function removePasskey(
    passkey: WebAuthnCredentialResponse,
    lastMethod: boolean,
  ) {
    const confirmed = await confirm(
      lastMethod
        ? backupCodesGoMessage
        : t`It won't work for logging in any more. You can also delete it from your device or password manager.`,
      t`Remove this passkey?`,
      { confirmLabel: t`Remove` },
    );

    if (!confirmed) {
      return;
    }

    await api.removePasskey(passkey.uuid);
    await refetch();
  }

  if (!status) {
    return (
      <FormRow
        kind="info"
        label={<Trans>Two-factor authentication</Trans>}
        data={
          statusError ? (
            <ErrorMessage message={errorText(statusError)} />
          ) : (
            <Spinner />
          )
        }
      />
    );
  }

  const methodCount = (status.totp_enrolled ? 1 : 0) + status.passkeys.length;
  // The backend refuses to take the last method away from an account
  // two-factor is required for.
  const removalBlocked =
    status.required && methodCount === 1
      ? t`Your account requires two-factor authentication, so its last method can't be removed.`
      : undefined;

  // Either method meets the requirement, so both rows carry the warning.
  const requiredWarning =
    status.required && methodCount === 0 ? <RequiredWarning /> : undefined;

  return (
    <>
      <FormRow
        kind="info"
        label={<Trans>Authenticator app</Trans>}
        hint={requiredWarning}
        data={
          status.totp_enrolled ? (
            <div className="flex items-center gap-3">
              <span>
                <Trans>Enabled</Trans>
              </span>
              {/* Enrolling again overwrites the secret — how a user moves to a
                  new phone, or recovers from losing one. */}
              <Button
                size="sm"
                onClick={() => {
                  setDialog({
                    kind: "enroll",
                    method: "totp",
                    replacing: true,
                  });
                }}
              >
                <Trans>Replace</Trans>
              </Button>
              <Tooltip content={removalBlocked}>
                <Button
                  size="sm"
                  destructive
                  disabled={removalBlocked !== undefined}
                  onClick={() => removeTotp(methodCount === 1)}
                >
                  <Trans>Remove</Trans>
                </Button>
              </Tooltip>
            </div>
          ) : (
            <Button
              onClick={() => {
                setDialog({ kind: "enroll", method: "totp", replacing: false });
              }}
            >
              <Trans>Set up</Trans>
            </Button>
          )
        }
      />

      <FormRow
        kind="info"
        label={<Trans>Passkeys</Trans>}
        hint={requiredWarning}
        data={
          <div className="flex flex-col items-start gap-3">
            {status.passkeys.length > 0 && (
              <PasskeyList
                passkeys={status.passkeys}
                onRemove={(passkey) =>
                  removePasskey(passkey, methodCount === 1)
                }
                removalBlocked={removalBlocked}
              />
            )}
            {/* Adding one needs the browser's support; listing and removing
                don't. */}
            <Tooltip content={passkeySupport.unsupportedReason}>
              <Button
                disabled={!passkeySupport.supported}
                onClick={() => {
                  setDialog({ kind: "enroll", method: "passkey" });
                }}
              >
                <Trans>Add a passkey</Trans>
              </Button>
            </Tooltip>
          </div>
        }
      />

      {methodCount > 0 && (
        <FormRow
          kind="info"
          label={<Trans>Backup codes</Trans>}
          data={
            <div className="flex items-center gap-3">
              <span>
                <Trans>{status.backup_codes_remaining} remaining</Trans>
              </span>
              <Button size="sm" onClick={regenerateBackupCodes}>
                <Trans>Regenerate</Trans>
              </Button>
            </div>
          }
        />
      )}

      {dialog.kind === "enroll" && (
        // Not dismissible by backdrop or Escape: leaving discards the setup, so
        // a stray click would kill a QR code the user may already have scanned
        // or close an open passkey prompt. Cancel is the only way out.
        <Dialog open>
          <DialogBody>
            {/* A floor, not a fixed width: the QR code sets this, but the
                backup codes shown at the end of enrolling are wider. */}
            <div className="min-w-96">
              <MfaEnrollment
                api={api}
                method={dialog.method}
                intro={
                  dialog.method === "totp"
                    ? totpIntro(dialog.replacing)
                    : undefined
                }
                expiredMessage={
                  <Trans>Setup expired before it was finished.</Trans>
                }
                onEnrolled={() => {
                  setDialog({ kind: "none" });
                  void refetch();
                }}
                onCancel={() => {
                  setDialog({ kind: "none" });
                }}
                cancelLabel={<Trans>Cancel</Trans>}
              />
            </div>
          </DialogBody>
        </Dialog>
      )}

      {dialog.kind === "backupCodes" && (
        <Dialog open>
          <DialogBody>
            <BackupCodes
              codes={dialog.codes}
              onContinue={() => {
                setDialog({ kind: "none" });
              }}
            />
          </DialogBody>
        </Dialog>
      )}
    </>
  );
}

/** What the QR-code screen says, depending on whether it replaces an app. */
function totpIntro(replacing: boolean) {
  return replacing ? (
    <Trans>
      Scan this QR code with your new authenticator app, then enter the code it
      shows. The old one stops working as soon as this is done.
    </Trans>
  ) : (
    <Trans>
      Scan this QR code with an authenticator app, then enter the code it shows.
    </Trans>
  );
}

/** Marks a method row while the account still owes a second factor. */
function RequiredWarning() {
  const { t } = useLingui();
  const text = t`Your account requires an authenticator app or a passkey.`;

  return (
    <Tooltip content={text}>
      <span className="inline-flex text-warning-foreground">
        <TriangleAlert size={16} role="img" aria-label={text} />
      </span>
    </Tooltip>
  );
}
